/**
 * Users section of the admin Settings dialog.
 *
 * Two panels:
 *   1. Current users  — everyone currently in Schedulers or Admins groups,
 *                       with toggle chips for each role
 *   2. Add user       — search the directory, click to add as Scheduler
 *                       (initial role; admin can promote them afterward)
 *
 * All operations are admin-only at the backend; this UI also only renders
 * when SettingsDialog is open (which itself is gated on isAdmin in App.tsx).
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { Loader2, Search, X, UserPlus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    listUsers,
    searchUsers,
    setUserRole,
    removeUserAccess,
    AppUser,
    DirectorySearchResult,
    AppRole,
} from '@/api/client';

interface UsersSectionProps {
    getToken: () => Promise<string>;
    onChanged: () => void;
}

export function UsersSection({ getToken, onChanged }: UsersSectionProps) {
    const [users, setUsers] = useState<AppUser[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [busyUserId, setBusyUserId] = useState<string | null>(null);

    const reload = useCallback(async () => {
        setLoadError(null);
        try {
            const token = await getToken();
            const r = await listUsers(token);
            setUsers(r.users);
        } catch (e: any) {
            setLoadError(e.message ?? 'Failed to load users');
        }
    }, [getToken]);

    useEffect(() => {
        reload();
    }, [reload]);

    /**
     * ---- ⛔⛤ SUPERSEDED 2026-09-21 (decision 207). §0.3 ----------------------
     *
     * ⛔⛤ ~~handleToggleRole(userId, role, currently)~~ — toggled one of two
     * independent chips, because Entra had two independent groups.
     *
     * ⛔⛔ `UX_UserCompanyRole_Company_Subject_App` ALLOWS ONE DOCK ROW PER
     * PERSON. "Scheduler + Admin" was never two grants — `Admin` is rank 100 and
     * clears Scheduler's threshold of 40, so it was one grant satisfying two
     * gates. A toggle model cannot express that, and the UI would let an admin
     * ask for a state the table refuses.
     *   ** A CONTROL THAT OFFERS A STATE THE STORE CANNOT HOLD IS A CONTROL
     *      THAT HAS TO FAIL, AND IT FAILS AT THE MOMENT SOMEBODY IS RELYING
     *      ON IT. **
     *
     * ⭐ So: one picker, two verbs. Setting a role creates, promotes, demotes or
     * reactivates — whichever the single row needs. Removing deactivates it.
     */
    const handleSetRole = async (userId: string, role: AppRole) => {
        setBusyUserId(userId);
        try {
            const token = await getToken();
            // ⚠️ NO name/email passed here. This person is ALREADY in the list,
            //    so their row already carries whatever name it has; the server
            //    COALESCEs rather than overwriting with null.
            await setUserRole(token, userId, role);
            await reload();
            onChanged();
        } catch (e: any) {
            setLoadError(e.message ?? 'Failed to update role');
        } finally {
            setBusyUserId(null);
        }
    };

    const handleRemove = async (userId: string) => {
        setBusyUserId(userId);
        try {
            const token = await getToken();
            await removeUserAccess(token, userId);
            await reload();
            onChanged();
        } catch (e: any) {
            // ⚠️ THE SERVER'S 400 FOR SELF-REMOVAL ARRIVES HERE AND MUST BE
            //    SHOWN AS IT IS WRITTEN. It names the reason ("ask another
            //    admin"), and replacing it with a generic string would turn a
            //    deliberate refusal into what looks like a bug.
            setLoadError(e.message ?? 'Failed to remove access');
        } finally {
            setBusyUserId(null);
        }
    };

    return (
        <div className="space-y-6">
            {/* Add a user */}
            <AddUserPanel
                getToken={getToken}
                existingUsers={users ?? []}
                onAdded={async () => {
                    await reload();
                    onChanged();
                }}
            />

            {/* Current users */}
            <div>
                <h3 className="font-medium text-sm mb-3">Current Users</h3>

                {loadError && (
                    <div className="text-sm rounded-md p-3 mb-3 bg-pipeline-rose-bg text-pipeline-rose-text border border-pipeline-rose-border">
                        {loadError}
                    </div>
                )}

                {users === null && !loadError ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading users...
                    </div>
                ) : users && users.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-4">
                        No users have access yet. Add someone using the search above.
                    </div>
                ) : (
                    <div className="border rounded-md divide-y">
                        {users?.map((u) => (
                            <UserRow
                                key={u.id}
                                user={u}
                                busy={busyUserId === u.id}
                                onSetRole={(role) => handleSetRole(u.id, role)}
                                onRemove={() => handleRemove(u.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ---- Current user row -----------------------------------------------------

function UserRow({
    user,
    busy,
    onSetRole,
    onRemove,
}: {
    user: AppUser;
    busy: boolean;
    onSetRole: (role: AppRole) => void;
    onRemove: () => void;
}) {
    /**
     * ⚠️ `roles` IS AN ARRAY AND HOLDS AT MOST ONE. The server keeps the array
     * shape so this file and the API could ship in either order; it never
     * returns two. ⛔ Reading `[0]` rather than `.includes(...)` is what makes
     * the single-row model visible here instead of implied.
     */
    const current: AppRole | null = user.roles[0] ?? null;

    return (
        <div className="flex items-center justify-between gap-4 px-3 py-2.5">
            <div className="min-w-0">
                <div className="text-sm font-medium truncate">{user.displayName}</div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
                {/*
                  * ⭐ ONE ROLE, CHOSEN — not two things switched on and off.
                  * ⚠️ Clicking the role somebody ALREADY holds is a no-op rather
                  *    than a toggle-off: a picker whose selected option removes
                  *    access on a second click is how somebody loses access by
                  *    double-clicking.
                  */}
                <RoleChip
                    label="Scheduler"
                    on={current === 'scheduler'}
                    disabled={busy || current === 'scheduler'}
                    onClick={() => onSetRole('scheduler')}
                />
                <RoleChip
                    label="Admin"
                    on={current === 'admin'}
                    disabled={busy || current === 'admin'}
                    onClick={() => onSetRole('admin')}
                />
                {/*
                  * ⛔ REMOVAL IS ITS OWN CONTROL, SEPARATED AND STYLED APART.
                  * It is the only destructive action on this row, and folding it
                  * into the role chips is what made the old toggle dangerous.
                  */}
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={busy}
                    title="Remove this person's Dock Scheduler access"
                    className={[
                        'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                        'bg-muted text-muted-foreground border border-transparent',
                        'hover:border-border hover:text-pipeline-rose-text',
                        busy && 'opacity-50 cursor-not-allowed',
                    ].filter(Boolean).join(' ')}
                >
                    <X className="h-3 w-3" />
                    Remove
                </button>
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            </div>
        </div>
    );
}

function RoleChip({
    label,
    on,
    disabled,
    onClick,
}: {
    label: string;
    on: boolean;
    disabled: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={[
                'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                on
                    ? 'bg-pipeline-emerald-bg text-pipeline-emerald-text border border-emerald-200'
                    : 'bg-muted text-muted-foreground border border-transparent hover:border-border',
                disabled && 'opacity-50 cursor-not-allowed',
            ].filter(Boolean).join(' ')}
            title={on ? `Remove ${label} role` : `Add ${label} role`}
        >
            {on && <Check className="h-3 w-3" />}
            {label}
        </button>
    );
}

// ---- Add user panel -------------------------------------------------------

function AddUserPanel({
    getToken,
    existingUsers,
    onAdded,
}: {
    getToken: () => Promise<string>;
    existingUsers: AppUser[];
    onAdded: () => Promise<void>;
}) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<DirectorySearchResult[] | null>(null);
    const [searching, setSearching] = useState(false);
    const [addingId, setAddingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Debounced search as the admin types
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (query.trim().length < 2) {
            setResults(null);
            setSearching(false);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            setError(null);
            try {
                const token = await getToken();
                const r = await searchUsers(token, query.trim());
                setResults(r.results);
            } catch (e: any) {
                setError(e.message ?? 'Search failed');
                setResults(null);
            } finally {
                setSearching(false);
            }
        }, 300);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query, getToken]);

    const handleAdd = async (user: DirectorySearchResult) => {
        setAddingId(user.id);
        setError(null);
        try {
            const token = await getToken();
            /**
             * ⭐⭐ THE NAME AND EMAIL ARE PASSED **HERE AND ONLY HERE**, and this
             * is the one call site where they exist.
             *
             * ⛔ Tokens on this tenant carry NO email claim — `/api/me` returns
             * `email: ""`, measured 2026-09-21 — so nothing downstream can ever
             * supply them. `UserCompanyRole.Email` and `DisplayName` are the only
             * place a readable name for this person will exist, and this Graph
             * search result is the only moment the app has held one.
             *
             *   ** A VALUE THAT IS FREE AT EXACTLY ONE MOMENT MUST BE CAPTURED
             *      AT THAT MOMENT, OR IT IS NOT MISSING DATA — IT IS PERMANENTLY
             *      MISSING DATA. **
             *
             * ⚠️ Without it every grant made from today reads "(no name)" for
             * ever, and the only way to diff this list against Entra is by object
             * id.
             */
            await setUserRole(token, user.id, 'scheduler', {
                email: user.email,
                displayName: user.displayName,
            });
            setQuery('');
            setResults(null);
            await onAdded();
        } catch (e: any) {
            setError(e.message ?? 'Failed to add user');
        } finally {
            setAddingId(null);
        }
    };

    const existingIds = new Set(existingUsers.map((u) => u.id));

    return (
        <div>
            <h3 className="font-medium text-sm mb-3">Add User</h3>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search Symbia directory by name or email..."
                    className="pl-9 pr-9"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => setQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label="Clear search"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {error && (
                <div className="text-sm rounded-md p-3 mt-3 bg-pipeline-rose-bg text-pipeline-rose-text border border-pipeline-rose-border">
                    {error}
                </div>
            )}

            {searching && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Searching...
                </div>
            )}

            {results !== null && !searching && (
                <div className="mt-3 border rounded-md max-h-72 overflow-y-auto">
                    {results.length === 0 ? (
                        <div className="text-sm text-muted-foreground p-4">
                            No users found matching "{query.trim()}".
                        </div>
                    ) : (
                        <div className="divide-y">
                            {results.map((r) => {
                                const alreadyHasAccess = existingIds.has(r.id);
                                return (
                                    <div
                                        key={r.id}
                                        className="flex items-center justify-between gap-3 px-3 py-2"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">
                                                {r.displayName}
                                            </div>
                                            <div className="text-xs text-muted-foreground truncate">
                                                {r.email}
                                            </div>
                                        </div>
                                        {alreadyHasAccess ? (
                                            <span className="text-xs text-muted-foreground flex-shrink-0">
                                                Already added
                                            </span>
                                        ) : (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleAdd(r)}
                                                disabled={addingId === r.id}
                                                className="flex-shrink-0"
                                            >
                                                {addingId === r.id ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <UserPlus className="h-3.5 w-3.5" />
                                                )}
                                                Add as Scheduler
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
